import { html } from 'lit';
import { querySelectorDeep } from 'omni-ui';

// Shared utilities
import { MediaOpsService, ToggleFeature as toggleFeature, trackEvent } from '../../../../shared';

// Feature imports
import { PlacementDetailsTableService } from '../../../placements';

/**
 * MediaOpsDropdown provides dropdown functionality for the MediaOps component
 * It's designed to be mixed into the MediaOps component
 */
export const CampaignTableV1Dropdown = {
  /**
   * Closes any open dropdown menus in the shadow DOM
   * @param {string} index - The index of the dropdown to keep open
   * @param {string} prefix - The prefix of the dropdown to keep open
   */
  closeMenu(index, prefix) {
    // Get all dropdowns on the page that aren't hoverable.
    // hide any opened dropdown
    let allDropdownEle = querySelectorDeep('omni-table', 'right-table')
      ?.shadowRoot?.querySelectorAll('.dropdown');
    allDropdownEle && allDropdownEle.forEach(ele => {
      if (ele.classList.contains('is-active') && ele.id !== (prefix + index)) {
        ele.classList.remove('is-active');
        ele.querySelector('button').classList.remove('is-active');
      }
    });
  },

  /**
   * Closes any open dropdown menus and removes portal dropdowns
   * @param {String} id - id of the current element
   * @param {String} prefix - prefix identifier
   */
  closeMenus(id, prefix) {
    // Close menu in shadow DOM
    this.closeMenu(id, prefix);
    
    // Remove any existing portal dropdowns
    document.querySelectorAll('.portal-dropdown').forEach(el => el.remove());
  },

  /**
   * Main entry point for handling document trigger clicks
   * Delegates to the appropriate implementation based on feature flag or browser support
   * @param {String} id - id of the documents || info options
   * @param {String} prefix - info || documents
   * @param {event} event - click event
   * @param {number} index - options popup index
   */
  onDocumentTriggerClick(id, prefix, event, index,value) {
    if (prefix === 'info') {
      trackEvent('More Info modal has been opened.', { access_from: 'Campaign Listing Page' });
    }
    
    try {
      // Close any open menus
      this.closeMenus(id, prefix);
      
      // Decide which implementation to use (could be based on feature flag or browser support)
      const usePortal = this.usePortalDropdowns({id, prefix, event, index});
      
      if (usePortal) {
        // Use the implementation that creates the dropdown outside Shadow DOM
        this.onDocumentTriggerClickShared(id, prefix, event, index, true,value);
      } else {
        // Use the original Shadow DOM implementation
        this.onDocumentTriggerClickShadowDom(id, prefix, event, index,value);
      }
      
      event.stopPropagation();
      event.preventDefault();
    } catch (error) {
      console.error("Error in onDocumentTriggerClick:", error);
    }
  },

  /**
   * Determines whether to use portal dropdowns based on feature flags or browser support
   * @returns {boolean} - Whether to use portal dropdowns
   */
  usePortalDropdowns() {
    // This can be based on a feature flag or browser support check
    // For now, always return true to use portals
    return true;
  },

  /**
   * Original Shadow DOM implementation for dropdown handling
   * @param {String} id - id of the element
   * @param {String} prefix - info || documents
   * @param {event} event - click event
   * @param {number} index - row index in the data
   */
  onDocumentTriggerClickShadowDom(id, prefix, event, index,value) {
    // First close any other open menus
    this.closeMenu(id, prefix);
    
    // Make the dropdown hide and show
    let el = querySelectorDeep('omni-table', 'right-table')?.shadowRoot?.querySelector('#' + prefix + '-' + id);
    const elButton = el?.querySelector('button');
    if (!el || !elButton) return;
    
    // Toggle active class
    el.classList.toggle('is-active');
    elButton.classList.toggle('is-active');
    
    // Get the dropdown menu element
    const dropdownMenu = el.querySelector('.dropdown-menu');
    if (!dropdownMenu || !el.classList.contains('is-active')) return;
    
    // Calculate position based on button location and available space
    const buttonRect = elButton.getBoundingClientRect();
    const isNearBottom = this.state.data.length > 6 && index > this.state.data.length - 4;
    
    // Set up scroll handler to reposition dropdown
    const scrollHandler = () => {
      const updatedButtonRect = elButton.getBoundingClientRect();
      dropdownMenu.style.position = 'fixed';
      dropdownMenu.style.zIndex = '10000';
      
      if (isNearBottom) {
        dropdownMenu.style.bottom = (window.innerHeight - updatedButtonRect.top + 10) + 'px';
        dropdownMenu.style.top = 'auto';
      } else {
        dropdownMenu.style.top = (updatedButtonRect.bottom + 5) + 'px';
        dropdownMenu.style.bottom = 'auto';
      }
      
      dropdownMenu.style.left = prefix === 'documents' ? 
        (updatedButtonRect.left - 145) + 'px' : 
        (updatedButtonRect.left - 560) + 'px';
    };
    
    // Initial positioning
    scrollHandler();
    
    // Add scroll event listeners
    const scrollableContainers = [
      window, 
      document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv
    ].filter(Boolean);
    
    scrollableContainers.forEach(container => {
      container.addEventListener('scroll', scrollHandler);
    });
    
    // Remove scroll event listeners when dropdown is closed
    const closeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target === el && !el.classList.contains('is-active')) {
          scrollableContainers.forEach(container => {
            container.removeEventListener('scroll', scrollHandler);
          });
          closeObserver.disconnect();
        }
      });
    });
    
    closeObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
  },

  /**
   * Shared code between implementations that handles toggling and positioning of dropdowns
   * with option to create outside of Shadow DOM
   * @param {String} id - id of the element
   * @param {String} prefix - info || documents
   * @param {event} event - click event
   * @param {number} index - row index in the data
   * @param {boolean} createOutsideShadowDom - Whether to create the dropdown outside of Shadow DOM
   */
  onDocumentTriggerClickShared(id, prefix, event, index, createOutsideShadowDom,value) {
    // make the dropdown hide and show
    let el = querySelectorDeep('omni-table', 'right-table')?.shadowRoot?.querySelector('#' + prefix + '-' + id);
    const elButton = el?.querySelector('button');
    if (!el || !elButton) return;
    
    el.classList.toggle('is-active');
    elButton.classList.toggle('is-active');
    
    // Get the dropdown menu and content
    let dropdownMenu = el.querySelector('.dropdown-menu');
    let dropdownContent = dropdownMenu?.querySelector('.dropdown-content');
    
    // Only proceed if we have elements and dropdown is now active
    if (el.classList.contains('is-active') && dropdownMenu && dropdownContent) {
      if (createOutsideShadowDom) {
        this.createPortalDropdown(el, elButton, dropdownMenu, dropdownContent, prefix, index,value);
      } else {
        // Move the dropdown to top for last 3 records
        if (this.state.data.length > 6 && index > this.state.data.length - 4 && !el.classList.contains('is-up')) {
          el.classList.add('is-up');
        }
        
        // Position the dropdown
        let dd = querySelectorDeep('omni-table', 'right-table').shadowRoot?.querySelectorAll('.dropdown-menu');
        dd.forEach(ele => {
          if (ele) {
            ele.style.left = prefix === 'documents' ? '-145px' : '-560px';
          }
        });
      }
    }
  },

  /**
   * Creates a dropdown menu outside of Shadow DOM to avoid styling limitations
   * @param {Element} el - The dropdown element
   * @param {Element} elButton - The button element
   * @param {Element} dropdownMenu - The dropdown menu element
   * @param {Element} dropdownContent - The dropdown content element
   * @param {String} prefix - info || documents
   * @param {number} index - row index in the data
   */
  createPortalDropdown(el, elButton, dropdownMenu, dropdownContent, prefix, index,value) {
    // Hide original dropdown menu
    const originalDisplayValue = dropdownMenu.style.display;
    dropdownMenu.style.display = 'none';
    
    // Create portal dropdown
    const portalDropdown = document.createElement('div');
    portalDropdown.className = 'portal-dropdown';
   
    // Handle content based on dropdown type
    if (prefix === 'info') {
      // Get current row data
     // const value = this.state.data[index];
      
      // Create structured info content with drag handle
       portalDropdown.innerHTML = `
         <div class="dropdown-header">
           <div class="header-title-container">
             <p class="dropdown-header-title">
             <span class="drag-icon">☰</span> More info</p>
           </div>
           <button class="close-button" aria-label="Close">×</button>
         </div>
         <div class="header-separator"></div>
         <section class="dropdown-section">
           <div class="dropdown-item">
             <p class="info-title">Source</p>
             <p class="info-value">${value?.source || ''}</p>
           </div>
           <div class="dropdown-item">
             <p class="info-title">Client</p>
             <p class="info-value">${value?.clientName || ''}</p>
           </div>
           <div class="dropdown-item">
             <p class="info-title">Submitted Version</p>
             <p class="info-value">${MediaOpsService.getSubmittedVersion(value?.filerevision) || ''}</p>
           </div>
         </section>
         <section class="dropdown-section creator-section">
           <div class="dropdown-item">
             <p class="info-title">Partners</p>
             <p class="info-value info-partner-warp">${MediaOpsService.getPartnerString(value) || ''}</p>
           </div>
         </section>
         <section class="dropdown-section creator-section file-name-section">
           <div class="dropdown-item">
            <p class="info-title">File name</p>
             <p class="info-value info-partner-warp">${value?.filename || ''}</p>
          </div>
        </section>
       `;
      
      // Make the entire header draggable
      const headerEl = portalDropdown.querySelector('.dropdown-header');
      if (headerEl) {
        headerEl.style.cursor = 'move';
        this.makeElementDraggable(portalDropdown, headerEl);
      }
      
      // Add close button event
      const closeButton = portalDropdown.querySelector('.close-button');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          portalDropdown.remove();
          el.classList.remove('is-active');
          elButton.classList.remove('is-active');
          dropdownMenu.style.display = originalDisplayValue;
        });
      }
    } else {
       //For documents dropdown, add drag handle and content
       const dragHandleDiv = document.createElement('div');
       dragHandleDiv.className = 'drag-handle';
       dragHandleDiv.innerHTML = '☰';
       dragHandleDiv.style.cursor = 'move';
       dragHandleDiv.style.textAlign = 'center';
       dragHandleDiv.style.padding = '5px';
       dragHandleDiv.style.borderBottom = '1px solid #f1f5fa';
      
      // Create content div
       const contentDiv = document.createElement('div');
       contentDiv.className = 'dropdown-content';
       contentDiv.innerHTML = dropdownContent.innerHTML;
      
       // Add to portal dropdown
       portalDropdown.appendChild(dragHandleDiv);
       portalDropdown.appendChild(contentDiv);
      
      // Add event listeners to handle clicks
      contentDiv.addEventListener('click', (e) => {
        const item = e.target.closest('.dropdown-item');
        if (!item) return;
        
        const actionText = item.textContent.trim();
        const originalItems = dropdownContent.querySelectorAll('.dropdown-item');
        
        // Find and click the original item
        originalItems.forEach(originalItem => {
          if (originalItem.textContent.trim() === actionText) {
            originalItem.click();
          }
        });
        
        // Clean up
        portalDropdown.remove();
        el.classList.remove('is-active');
        elButton.classList.remove('is-active');
        dropdownMenu.style.display = originalDisplayValue;
      });
    }
    
    // Set initial position based on button rectangle
    const buttonRect = elButton.getBoundingClientRect();
    const isNearBottom = this.state.data.length > 6 && index > this.state.data.length - 5;
    
    // Create style based on dropdown type and position
    portalDropdown.style.position = 'fixed';
    portalDropdown.style.zIndex = '10000';
    portalDropdown.style.background = 'white';
    portalDropdown.style.borderRadius = '10px';
    portalDropdown.style.boxShadow = '0 24px 31px -6px rgba(1, 33, 78, 0.37), 0 2px 9px -1px rgba(1, 33, 78, 0.1)';
    portalDropdown.style.border = 'solid 1px #f1f5fa';
    portalDropdown.style.padding = prefix === 'info' ? '0' : '10px';
    portalDropdown.style.minWidth = prefix === 'info' ? '600px' : '150px';
    portalDropdown.style.minHeight = prefix === 'info' ? '246px' : 'auto';
    
    if (isNearBottom) {
      portalDropdown.style.bottom = `${window.innerHeight - buttonRect.top + 10}px`;
      portalDropdown.style.top = 'auto';
    } else {
      portalDropdown.style.top = `${buttonRect.bottom + 5}px`;
      portalDropdown.style.bottom = 'auto';
    }
    
    portalDropdown.style.left = `${prefix === 'documents' ? 
      buttonRect.left - 145 : buttonRect.left - 560}px`;
    
    // Add styles for portal dropdowns
    this.addPortalDropdownStyles();
    
    // Make the dropdown draggable - Fix: Use makeElementDraggable instead of makeDropdownDraggable
    if (prefix === 'info') {
      // No need for additional function call here since we're already making the header draggable
      // The header element is handled in the info section
    } else {
      // For documents dropdown, make the drag handle the trigger for dragging
      const dragHandle = portalDropdown.querySelector('.drag-handle');
      if (dragHandle) {
        this.makeElementDraggable(portalDropdown, dragHandle);
      }
    }
    
    // Keep scroll event listeners to update position (for both types, as we want position updates during scroll)
    const scrollableContainers = [
      window, 
      document.getElementsByClassName("campaign-tile")[0]?.bodyScrollerDiv
    ].filter(Boolean);
    
    const updatePosition = () => {
      // Only update position if not being dragged
      if (!portalDropdown._isDragging) {
        const updatedButtonRect = elButton.getBoundingClientRect();
        
        if (isNearBottom) {
          portalDropdown.style.bottom = `${window.innerHeight - updatedButtonRect.top + 10}px`;
          portalDropdown.style.top = 'auto';
        } else {
          portalDropdown.style.top = `${updatedButtonRect.bottom + 5}px`;
          portalDropdown.style.bottom = 'auto';
        }
        
        portalDropdown.style.left = `${prefix === 'documents' ? 
          updatedButtonRect.left - 145 : updatedButtonRect.left - 560}px`;
      }
    };
    
    // Add scroll event listeners
    scrollableContainers.forEach(container => {
      container.addEventListener('scroll', updatePosition);
    });
    
    // Close when clicking outside and remove scroll listeners
    const closeOnClickOutside = (e) => {
      if (!portalDropdown.contains(e.target) && !el.contains(e.composedPath()[0])) {
        portalDropdown.remove();
        el.classList.remove('is-active');
        elButton.classList.remove('is-active');
        dropdownMenu.style.display = originalDisplayValue;
        document.removeEventListener('click', closeOnClickOutside);
        
        scrollableContainers.forEach(container => {
          container.removeEventListener('scroll', updatePosition);
        });
      }
    };
    document.addEventListener('click', closeOnClickOutside);
    
    // Append to body
    document.body.appendChild(portalDropdown);
  },

  /**
   * Makes a dropdown element draggable using a specified handle
   * @param {HTMLElement} element - The element to make draggable
   * @param {HTMLElement} handle - The handle element that triggers dragging
   */
  makeElementDraggable(element, handle) {
    if (!element || !handle) return;
    
    let isDragging = false;
    let offsetX, offsetY;
    
    // Store dragging state on the element
    element._isDragging = false;
    
    // Mouse down event on handle starts the drag
    handle.addEventListener('mousedown', (e) => {
      // Don't start drag if we clicked on a button or other interactive element
      if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
        return;
      }
      
      isDragging = true;
      element._isDragging = true;
      
      // Get the current position of the element
      const rect = element.getBoundingClientRect();
      
      // Calculate the offset of the mouse relative to the element
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;
      
      // Change cursor during dragging
      element.style.cursor = 'grabbing';
      
      // Prevent text selection during dragging
      e.preventDefault();
    });
    
    // Mouse move event updates position during drag
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      
      // Calculate new position
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;
      
      // Set new position
      element.style.left = `${newX}px`;
      element.style.top = `${newY}px`;
      
      // Prevent element from being dragged off-screen
      const rect = element.getBoundingClientRect();
      
      if (rect.left < 0) element.style.left = '0px';
      if (rect.top < 0) element.style.top = '0px';
      if (rect.right > window.innerWidth) element.style.left = `${window.innerWidth - rect.width}px`;
      if (rect.bottom > window.innerHeight) element.style.top = `${window.innerHeight - rect.height}px`;
    });
    
    // Mouse up event ends dragging
    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        element._isDragging = false;
        element.style.cursor = '';
      }
    });
  },

  /**
   * Adds required styles for the portal dropdown if they don't already exist
   */
  addPortalDropdownStyles() {
    if (!document.getElementById('portal-dropdown-styles')) {
      const style = document.createElement('style');
      style.id = 'portal-dropdown-styles';
      style.textContent = `
        /* General styles */
        .portal-dropdown {
          font-family: inherit;
          color: #3b3e3f;
        }
        
        /* Documents dropdown */
        .portal-dropdown .dropdown-item {
          color: #3b3e3f;
          font-size: 14px;
          padding: 8px;
          display: block;
          text-decoration: none;
          cursor: pointer;
        }
        .portal-dropdown .dropdown-item:hover {
          background-color: #f5f5f5;
        }
        
        /* Drag handle for documents */
        .portal-dropdown > .drag-handle {
          cursor: move;
          text-align: center;
          padding: 5px;
          border-bottom: 1px solid #f1f5fa;
          background-color: #f9fbfd;
          border-radius: 8px 8px 0 0;
        }
        
        /* Info dropdown */
        .portal-dropdown .dropdown-header {
          padding: 0.375rem 1rem;
          background-color: #fff;
          border-bottom: 1px solid #edf0f4;
          border-radius: 10px 10px 0 0;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 41px;
        }
        
        /* Drag handle container for info dropdown */
        .portal-dropdown .header-title-container {
          display: flex;
          align-items: center;
        }
        
        /* Drag icon styling */
        .portal-dropdown .drag-icon {
          cursor: move;
          margin-right: 8px;
          font-size: 16px;
          color: #6c757d;
          display: inline-block;
        }
        
        .portal-dropdown .dropdown-header-title {
          margin: 0;
          font-size: 14px;
          color: #3b3e3f;
        }
        .portal-dropdown .close-button {
          border: none;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          background-color: transparent;
        }
        .portal-dropdown .dropdown-section {
          padding: 28px 30px 0px;
          display: flex;
        }
        .portal-dropdown .creator-section {
          margin-bottom: 10px;
        }
        .portal-dropdown .file-name-section {
          padding-bottom: 20px;
        }
        .portal-dropdown .dropdown-section .dropdown-item {
          margin-right: 40px;
        }
        .portal-dropdown .info-title {
          opacity: 0.6;
          font-size: 14px;
          color: #3b3e3f;
          padding-bottom: 6px;
          margin: 0;
        }
        .portal-dropdown .info-value {
          font-size: 14px;
          color: #3b3e3f;
          padding-bottom: 6px;
          margin: 0;
        }
        .portal-dropdown .info-partner-warp {
          word-break: break-all;
          white-space: normal;
        }
      `;
      document.head.appendChild(style);
    }
  },

  /**
   * Generates the HTML for the info dialog in the Actions column
   * @param {Object} value - The campaign object
   * @param {string} campaignIdWithoutSpaces - The campaign ID without spaces
   * @param {number} index - The index of the row
   * @returns {html} The HTML template for the info dialog
   */
  getInfoDialogHTML(value, campaignIdWithoutSpaces, index) {
    return html`
    <style>
      :host td {
        overflow: revert !important;
      }
      :host::part(dropdown-header) {
        padding: 0.375rem 1rem;
        background-color: #fff;
        border-bottom: 1px solid #edf0f4;
        border-radius: 10px 10px 0 0;
        font-size: 14px !important;
        display: flex !important;
        align-items: center;
        justify-content: space-between;
        height: 41px;
      }
      :host::part(dropdown-content) {
        display: flex;
        flex-direction: column;
        font-size: 14px;
        border-radius: 10px;
        box-shadow: 0 24px 31px -6px rgba(1, 33, 78, 0.37), 0 2px 9px -1px rgba(1, 33, 78, 0.1);
        border: solid 1px #f1f5fa;
        background-color: #fff;
        min-width: 600px;
        min-height: 246px;
        height: auto;
        padding: 0 0 30px 0;
      }
      :host::part(dropdown-section) {
        padding: 28px 30px 0px !important;
        display: flex !important;
      }
      :host::part(creator-section) {
        margin-bottom: 10px;
      }
      :host::part(info-title) {
        opacity: 0.6;
        font-size: 14px;
        color: #3b3e3f;
        padding-bottom: 6px;
      }
      :host::part(dropdown-item) {
        margin-right: 40px;
      }
      :host::part(info-value) {
        font-size: 14px;
        color: #3b3e3f;
        padding-bottom: 6px;
      }
      :host::part(info-partner-warp) {
        word-break: break-all;
        white-space: normal;
      }
      :host::part(file-name-section) {
        padding-bottom: 20px !important;
      }
    </style>
    <div part="info-dialog" id="info-${campaignIdWithoutSpaces}" style="margin-right: 20px" class=" dropdown" >
      <div class="dropdown-trigger">
        <omni-tooltip-next>
          <button slot="invoker" aria-haspopup="true" class="icon" 
            @click="${(e) => this.onDocumentTriggerClick(campaignIdWithoutSpaces, 'info', e, index,value)}">
            <omni-icon class="is-size-4 cursor" icon-id="omni:informative:info"></omni-icon>
          </button>
          <p slot="content">More info</p>
        </omni-tooltip-next>
      </div>
      <div part="dropdown-menu" class="dropdown-menu" id="dropdown-menu" role="menu">
        <div part="dropdown-content" class="dropdown-content" style="display: flex;">
          <section part="dropdown-header">
            <p class="object-title" part="dropdown-header-title info-value" >More info</p>
            <button aria-haspopup="true" class="icon" part="dropdown-header-close-button"  aria-label="Close" onClick={this.close}>
              <omni-icon class="is-size-1 info-icon has-text-almost-black" part="dropdown-header-close-button-icon"
                icon-id="omni:interactive:close"
              ></omni-icon>
            </button>
          </section>
          <div part="header-seperator"></div>
          <section part="dropdown-section" class="dropdown-item">
            <div part="dropdown-item">
              <p part="info-title" class="has-text-left">Source</p>
              <p part="info-value" class="has-text-left">${value?.source}</p>
            </div>
            <div part="dropdown-item">
              <p part="info-title" class="has-text-left">Client</p>
              <p part="info-value" class="has-text-left">${value?.clientName}</p>
            </div>
            <div part="dropdown-item">
              <p part="info-title" class="has-text-left">Submitted Version</p>
              <p class="has-text-left">${MediaOpsService.getSubmittedVersion(value?.filerevision)}</p>
            </div>
          </section>
          <section part="dropdown-section creator-section" class="dropdown-item">
            <div part="dropdown-item">
              <p part="info-title" class="has-text-left">Partners</p>
              <p class="info-value info-partner-warp" class="has-text-left">${MediaOpsService.getPartnerString(value)}</p>
            </div>
          </section>
          <section part="dropdown-section creator-section file-name-section" class="dropdown-item">
            <div part="dropdown-item">
              <p part="info-title" class="has-text-left">File name</p>
              <p class="info-value info-partner-warp" class="has-text-left">${value?.filename}</p>
            </div>
          </section>
        </div>
      </div>
    </div>`;
  },

  /**
   * Generates the HTML for the documents dialog and actions in the Actions column
   * @param {Object} value - The campaign object
   * @param {string} campaignIdWithoutSpaces - The campaign ID without spaces
   * @param {number} index - The index of the row
   * @returns {html} The HTML template for the actions dialog
   */
  getActionsDialogHTML(value, campaignIdWithoutSpaces, index) {
    return html`
    <style>
      :host::part(action-dropdown-item){
        color: #3b3e3f;
        font-size: 14px;
      }
      :host::part(disabled){
        pointer-events: none;
        cursor: not-allowed !important;
        color: var(--color-core-medium) !important;
      }
    </style>
    <div id="documents-${campaignIdWithoutSpaces}" class="dropdown ${this.isDocumentMenuOpen ? 'is-active' : ''}">
      <div class="dropdown-trigger">
        <omni-tooltip-next>
          <button slot="invoker" aria-haspopup="true" class="icon" @click=${(e) =>
            this.onDocumentTriggerClick(campaignIdWithoutSpaces, 'documents', e, index,value)}>
            <omni-icon class="is-size-4 cursor" icon-id="omni:informative:files"></omni-icon>
          </button>
          <p slot="content">Documents</p>
        </omni-tooltip-next>
      </div>
      <div part= "dropdown-menu" class="dropdown-menu" id="dropdown-menu" role="menu">
        <div class="dropdown-content">
          ${html`
              ${toggleFeature.RFP ? html`<a part="action-dropdown-item" class="dropdown-item" @click="${this.navigateToPath.bind(this, value, 'rfpDetailsTable')}">RFP</a>` : ''}
              <a part="action-dropdown-item" class="dropdown-item" @click="${this.navigateToPath.bind(this, value, 'placementDetailsTable')}">Placements</a>
              <a part="action-dropdown-item" class="dropdown-item" @click="${this.exportPlacements.bind(this, value)}">Export placements</a>
              <a part="action-dropdown-item" class="dropdown-item" @click="${this.archiveCampaign.bind(this, value)}">Archive campaign</a>
              ${value.exportStatus?.includes("Error") ? html`<a part="action-dropdown-item" class="dropdown-item" @click="${() => PlacementDetailsTableService.copyTextToClipboard(value.exportStatusError)}">Copy error details</a>` : ''}
              ${value.nestedData || value.data_source?.toLowerCase() === 'mediaocean' ? html`<a part="action-dropdown-item" class="dropdown-item" @click="${() => this.unmergeCampaigns(value)}">
                ${value?.nestedData ? `Unmerge All` : (value.data_source?.toLowerCase() === 'mediaocean' ? `Unmerge` : ``)}
              </a>` : ''}
          `}
        </div>
      </div>
    </div>`;
  },

  /**
   * Generates the complete HTML for the Actions column
   * @param {Object} value - The campaign object
   * @param {number} index - The index of the row
   * @returns {html} The HTML template for the actions column
   */
  getActionsColumnHTML(value, index) {
    const campaignIdWithoutSpaces = value?.id.replace(/[^a-zA-Z0-9]/g, '');
    const infoHtmlStr = this.getInfoDialogHTML(value, campaignIdWithoutSpaces, index);
    const actionsHtmlStr = this.getActionsDialogHTML(value, campaignIdWithoutSpaces, index);

    return html`
    <td part="table-body-cell">
      <div style="display: flex;justify-content: center;">
        ${infoHtmlStr}
        ${actionsHtmlStr}
      </div>
    </td>`;
  },

  /**
   * Generates the HTML for the restore button in archive tab
   * @param {Object} value - The campaign object
   * @returns {html} The HTML template for the restore button
   */
  getRestoreButtonHTML(value) {
    return html`
    <style>
      :host::part(action-cell){
        padding-left: 10px;
      }
    </style>
    <td part="table-body-cell action-cell">
      <button part="restore-button" class="secondary medium" @click="${this.unArchiveCampaign.bind(this, value)}">Restore</button>
    </td>`;
  }
};
